import classnames from "classnames";
import copy from "copy-to-clipboard";
import omit from "omit.js";
// import PropTypes from 'prop-types';
import React, {cloneElement, useState, useEffect, useCallback} from "react";
import {getNid, prefix, WebUI} from "../../wui-core/src/index";
import Icon from "../../wui-icon/src";
import Message from "../../wui-message/src";

import {Collapse} from "../../wui-transition/src/index";

import {PanelProps} from "./iCollapse";

const defaultProps = {
    collapsible: "",
    clsPrefix: "wui-collapse",
    colors: "default",
    bordered: false,
    ghost: false,
    showArrow: true,
    expandIconPosition: "left",
    copyable: false,
    onEnter: () => {},
    onEntering: () => {},
    onEntered: () => {},
    onExit: () => {},
    onExiting: () => {},
    onExited: () => {},
    header: null,
    footer: null,
    headerStyle: {},
    footerStyle: {},
    eventKey: "",
    onSelect: null,
    expandIcon: null,
    extra: null,
    forceRender: true
};
function getChildrenNode(elem: any) {
    let selectValue: any = null;
    const dep = (options: any) => {
        if (!options) {
            return;
        }
        if (options?.classList?.value?.includes("wui-collapse-header-only-icon")) {
            selectValue = options;
        } else {
            if (options?.parentNode) {
                dep(options?.parentNode);
            }
        }
    };
    dep(elem);
    return selectValue;
}

const Panel: React.FC<PanelProps> = (props) => {
    const [expanded, setExpanded] = useState<boolean | undefined>(() => {
        return props.defaultExpanded !== undefined ? props.defaultExpanded : props.expanded;
    });

    // 替换componentDidUpdate，处理expanded属性变化
    useEffect(() => {
        if (props.parentFlag) {
            if (
                "expanded" in props &&
                "parentActiveKey" in props &&
                props?.parentActiveKey != null &&
                props.expanded !== expanded
            ) {
                setExpanded(props.expanded);
            }
        } else {
            if ("expanded" in props && props.expanded !== expanded) {
                setExpanded(props.expanded);
            }
        }
    }, [props.expanded, props.parentFlag, props.parentActiveKey, expanded]);

    // 展开/收起处理
    const expandHandle = useCallback((e: any) => {
        if (props.onSelect) {
            props.onSelect(props.eventKey, props.expanded as boolean, e);
        }

        // 传入activeKey时受控
        if (props.parentFlag && props.parentActiveKey) {
            return;
        }
        if (e.selected) {
            setExpanded(!expanded);
        }
    }, [props.onSelect, props.eventKey, props.expanded, props.parentFlag, props.parentActiveKey, expanded]);

    // 头部点击事件
    const handleClickTitle = useCallback((e: any) => {
        // 不让事件进入事件池
        e.persist();
        e.selected = true;
        let {collapsible, clsPrefix, type} = props;
        if (collapsible === "disabled") {
            return;
        } else if (collapsible === "header") {
            if (e.target.className === `collapsible-header` || e.target.className === `${clsPrefix}-title`) {
                expandHandle(e);
            }
        } else if (collapsible === "icon") {
            if (getChildrenNode(e.target)) {
                expandHandle(e);
            }
            if (type === "card" || type === "list") {
                if (e.target.className.includes(`${clsPrefix}-header-icon`)) {
                    expandHandle(e);
                }
            }
        } else {
            expandHandle(e);
        }
    }, [props, expandHandle]);

    // 渲染panelheader
    const renderHeader = useCallback((
        collapsible: string,
        header: React.ReactNode,
        id: string,
        role: string,
        currentExpanded: boolean,
        clsPrefix: string
    ) => {
        const titleClassName = `${clsPrefix}-title`;

        if (!collapsible && collapsible !== "" && collapsible !== "header" && collapsible !== "icon") {
            if (!React.isValidElement(header)) {
                return header;
            }

            return cloneElement(header, {
                // @ts-ignore
                className: classnames(header.props.className, titleClassName)
            });
        }

        if (!React.isValidElement(header)) {
            return (
                <h4 role='presentation' className={titleClassName}>
                    {renderAnchor(header, id, role, currentExpanded)}
                </h4>
            );
        }
        if (props.headerContent) {
            return cloneElement(header, {
                // @ts-ignore
                className: classnames(header.props.className, titleClassName)
            });
        }

        return cloneElement(header, {
            // @ts-ignore
            className: classnames(header.props.className, titleClassName),
            children: renderAnchor(header.props.children, id, role, currentExpanded)
        });
    }, [props.headerContent]);

    // 如果使用链接，渲染为a标签
    const renderAnchor = useCallback((header: React.ReactNode, id: string, role: string, currentExpanded: boolean) => {
        return (
            <a
                role={role}
                href={id && `#${id}`}
                aria-controls={id}
                aria-expanded={currentExpanded}
                aria-selected={currentExpanded}
                className={"collapsible-header"}
                // @ts-ignore
                uiclickable={props.uirunmode === "design" ? "true" : undefined}
            >
                {header}
            </a>
        );
    }, [props.uirunmode]);

    // 复制代码，弹出提示信息
    const copyDemo = useCallback((e: any) => {
        let panelTarget = e.target.parentNode;
        let clipBoardContent = panelTarget.firstChild.innerText;
        copy(clipBoardContent);
        Message.create({content: "复制成功！", color: "success", duration: 2});
    }, []);

    // 如果有折叠动画，渲染折叠动画
    const renderCollapsibleBody = useCallback((
        id: string,
        currentExpanded: boolean,
        role: string,
        children: React.ReactElement,
        clsPrefix: string,
        copyable: boolean,
        animationHooks: any,
        forceRender: boolean,
        destroyInactivePanel: boolean,
        collapsible: string,
        header: React.ReactNode,
        bodyClassName: string
    ) => {
        let adapterNid = getNid(props); // 适配nid、uitype
        return (
            <Collapse in={currentExpanded} {...animationHooks}>
                <div
                    id={id}
                    role={role}
                    className={`${clsPrefix}-collapse`}
                    aria-hidden={!currentExpanded}
                    // @ts-ignore
                    nid={adapterNid?.nid ? adapterNid?.nid + "_body" : props?.nid}
                    uitype={adapterNid?.uitype}
                >
                    {renderBody(
                        children,
                        clsPrefix,
                        copyable,
                        forceRender,
                        currentExpanded,
                        destroyInactivePanel,
                        collapsible,
                        header,
                        bodyClassName
                    )}
                </div>
            </Collapse>
        );
    }, [props]);

    // 渲染panelbody
    const renderBody = useCallback((
        rawChildren: React.ReactElement,
        clsPrefix: string,
        copyable: boolean,
        forceRender: boolean,
        currentExpanded: boolean,
        destroyInactivePanel: boolean,
        collapsible: string,
        _header: any,
        bodyClassName: string
    ) => {
        let {type} = props;
        const children: any[] = [];
        let bodyChildren: any[] = [];

        const bodyClassNameAll = classnames({
            [`${clsPrefix}-body`]: true,
            [`${bodyClassName}`]: bodyClassName,
            [`${clsPrefix}-type-body-${type}`]: type
        });
        let destroyInactivePanelFlag =
            collapsible === "disabled"
                ? {display: "none"}
                : currentExpanded
                    ? undefined
                    : destroyInactivePanel
                        ? {display: "none"}
                        : undefined;

        // 添加到body的children中
        const maybeAddBody = () => {
            if (!bodyChildren.length) {
                return;
            }
            // 给子组件添加key，为了之后触发事件时使用
            // 面板收起时destroyInactivePanel为true则销毁
            let childrenBodyDom =
                !currentExpanded && destroyInactivePanel ? null : (
                    <div key={children.length} className={bodyClassNameAll} style={destroyInactivePanelFlag}>
                        {bodyChildren}
                        {copyable && <i className={`${clsPrefix}-copy uf uf-files-o`} onClick={copyDemo}></i>}
                    </div>
                );
            let childrenBody = currentExpanded
                ? childrenBodyDom
                : collapsible !== "disabled"
                    ? forceRender
                        ? childrenBodyDom
                        : null
                    : childrenBodyDom;
            children.push(childrenBody);
            bodyChildren = [];
        };

        // 转换为数组，方便复用
        React.Children.toArray(rawChildren).forEach(child => {
            if (React.isValidElement(child) && child.props.fill) {
                maybeAddBody();

                // 将标示fill设置为undefined
                // @ts-ignore
                children.push(cloneElement(child, {fill: undefined}));

                return;
            }
            bodyChildren.push(child);
        });

        maybeAddBody();

        return children;
    }, [props.type, copyDemo]);

    const {
        collapsible,
        header,
        id,
        footer,
        expanded: propsExpanded,
        footerStyle,
        headerStyle,
        headerRole,
        panelRole,
        className,
        colors,
        children,
        onEnter,
        onEntering,
        onEntered,
        clsPrefix,
        onExit,
        onExiting,
        onExited,
        eventKey,
        copyable,
        bordered,
        ghost,
        showArrow,
        expandIconPosition,
        extra,
        expandIcon,
        forceRender,
        destroyInactivePanel,
        accordion,
        bodyClassName,
        fieldid,
        type,
        ...otherProps
    } = props;

    let currentExpanded;
    if (accordion) {
        currentExpanded = propsExpanded != null ? propsExpanded : expanded;
    } else {
        currentExpanded = expanded;
    }

    let classes = {};
    classes = {
        [`${clsPrefix}`]: true,
        [`${clsPrefix}-${colors}`]: true
    };
    if (bordered) {
        Object.assign(classes, {
            [`${clsPrefix}-borderless`]: true
        });
    }
    if (ghost) {
        Object.assign(classes, {
            [`${clsPrefix}-ghost`]: true
        });
    }
    if (showArrow) {
        Object.assign(classes, {
            [`${clsPrefix}-arrow`]: true
        });
    }
    if (expandIconPosition === "left") {
        Object.assign(classes, {
            [`${clsPrefix}-icon-position-left`]: true
        });
    } else if (expandIconPosition === "right") {
        Object.assign(classes, {
            [`${clsPrefix}-icon-position-right`]: true
        });
    }
    let showClassName = currentExpanded ? `${prefix}-panel-show` : `${prefix}-panel-hidden`;
    Object.assign(classes, {
        [`${showClassName}`]: true
    });
    const headerClass = {
        [`${clsPrefix}-heading`]: true,
        [`${clsPrefix}-type-${type}`]: type
    };

    copyable !== false;
    const iconActiveStyle =
        collapsible !== "disabled"
            ? currentExpanded
                ? `${clsPrefix}-icon-down`
                : `${clsPrefix}-icon-right`
            : `${clsPrefix}-icon-right`;
    let collapsibleStyle = collapsible === "disabled" ? `${clsPrefix}-item-disabled` : "";
    Object.assign(classes, {
        [`${collapsibleStyle}`]: true
    });
    let classIcon = {};
    classIcon = {
        [`${clsPrefix}-header-icon`]: true,
        [`${clsPrefix}-header-only-icon`]: true,
        [`${iconActiveStyle}`]: true
    };
    let isActive = iconActiveStyle === `${clsPrefix}-icon-right`;
    let panelProps = Object.assign({}, props);
    Object.assign(panelProps, {isActive});
    // 这里用cloneElement是为了给自定义传进的icon添加className以实现点击交互效果
    const iconDom =
        expandIcon !== null ? (
            typeof expandIcon == "function" ? (
                cloneElement(expandIcon(panelProps), {
                    className: classnames(className, {
                        [`${expandIcon(panelProps)?.props?.className}`]: expandIcon(panelProps)?.props?.className,
                        [`${clsPrefix}-header-only-icon`]: true
                    })
                })
            ) : (
                cloneElement(expandIcon, {
                    className: classnames(className, classIcon)
                })
            )
        ) : type === "list" ? (
            <Icon
                className={`${clsPrefix}-header-icon ${iconActiveStyle}`}
                type='uf-erjifenzuzhankai'
                // @ts-ignore
                uiclickable={otherProps.uirunmode === "design" ? "true" : undefined}
            />
        ) : // @ts-ignore
            type === "card" ? (
                <Icon
                    className={`${clsPrefix}-header-icon ${iconActiveStyle}`}
                    type='uf-zhedie-zhankai'
                    // @ts-ignore
                    uiclickable={otherProps.uirunmode === "design" ? "true" : undefined}
                />
            ) : (
            // @ts-ignore
                <Icon
                    className={`${clsPrefix}-header-icon ${clsPrefix}-header-only-icon ${iconActiveStyle}`}
                    type='uf-arrow-down'
                    // @ts-ignore
                    uiclickable={otherProps.uirunmode === "design" ? "true" : undefined}
                />
            );

    // 当collapsible为header时添加的className
    let collapsibleClassName = `${clsPrefix}-collapsible-header`;
    let adapterNid = getNid(props);
    let typeLine = <span className={`${clsPrefix}-header-list`}></span>;

    return (
        <div
            {...omit(otherProps, ["defaultActiveKey", "onSelect", "headerContent", "defaultExpanded", "onChange", "parentFlag", "parentActiveKey"])}
            className={classnames(className, classes)}
            id={collapsible !== "disabled" ? undefined : id}
            fieldid={fieldid && !props?.parentFlag ? fieldid : undefined}
            {...adapterNid}
        >
            {header && (
                <div
                    className={classnames(headerClass)}
                    style={headerStyle}
                    onClick={handleClickTitle}
                    fieldid={fieldid ? fieldid + "-panel_" + eventKey || "" : undefined}
                    // @ts-ignore
                    uiclickable={otherProps.uirunmode === "design" ? "true" : undefined}
                >
                    {type === "lineFirstLevel" || type === "lineSecondLevel" ? (
                        <>
                            {collapsible === "header" ? (
                                <span className={collapsibleClassName}>
                                    {renderHeader(
                                        collapsible,
                                        header,
                                        id as string,
                                        headerRole as string,
                                        currentExpanded as boolean,
                                        clsPrefix as string
                                    )}
                                </span>
                            ) : (
                                renderHeader(
                                    collapsible as string,
                                    header,
                                    id as string,
                                    headerRole as string,
                                    currentExpanded as boolean,
                                    clsPrefix as string
                                )
                            )}
                            {showArrow && iconDom}
                            {extra && <div className={`${clsPrefix}-extra`} onClick={e=>e.stopPropagation()}>{extra}</div>}
                        </>
                    ) : (
                        <>
                            {showArrow && iconDom}
                            {collapsible === "header" ? (
                                <span className={collapsibleClassName}>
                                    {renderHeader(
                                        collapsible,
                                        header,
                                        id as string,
                                        headerRole as string,
                                        currentExpanded as boolean,
                                        clsPrefix as string
                                    )}
                                </span>
                            ) : (
                                renderHeader(
                                    collapsible as string,
                                    header,
                                    id as string,
                                    headerRole as string,
                                    currentExpanded as boolean,
                                    clsPrefix as string
                                )
                            )}
                            {type === "list" && typeLine}
                            {extra && <div className={`${clsPrefix}-extra`} onClick={e=>e.stopPropagation()}>{extra}</div>}
                        </>
                    )}
                </div>
            )}

            {collapsible !== "disabled"
                ? renderCollapsibleBody(
                      id as string,
                      currentExpanded as boolean,
                      panelRole as string,
                      children as React.ReactElement,
                      clsPrefix as string,
                      copyable as boolean,
                      {
                          onEnter,
                          onEntering,
                          onEntered,
                          onExit,
                          onExiting,
                          onExited
                      },
                      forceRender as boolean,
                      destroyInactivePanel as boolean,
                      collapsible as string,
                      header,
                      bodyClassName as string
                )
                : renderBody(
                      children as React.ReactElement,
                      clsPrefix as string,
                      copyable as boolean,
                      forceRender as boolean,
                      currentExpanded as boolean,
                      destroyInactivePanel as boolean,
                      collapsible,
                      header,
                      bodyClassName as string
                )}

            {footer && (
                <div className={`${clsPrefix}-footer`} style={footerStyle}>
                    {footer}
                </div>
            )}
        </div>
    );
};

export default WebUI({name: "collapse", defaultProps})(Panel);
